/**
 * 「目标」——每一门离目标还有多远。
 *
 * 每张卡就是一条进度：科目名 + 当前分 / 目标分 + 一根粗进度条 + 考期倒数。
 * 分数用最大的字号，因为那是他真正在意的数字；其余全部退成灰。
 * 展开才出现录入区 —— 平时不占地方，要用时一点即出。
 */

import { useCallback, useEffect, useState } from 'react'
import { api } from '../lib/api'

interface Subject {
  id: number; kind: string; name: string; placeholder: boolean; status: string
  targetScore: string | null; targetStretch: string | null; note: string | null
  sections: { section: string; score: string; ratio: number | null }[]
  examDate: string | null; daysToExam: number | null
  scale: { min: number; max: number; step: number } | null
  latestScore: { score: string; official: boolean; takenAt: string | null } | null
  ratio: number | null; targetRatio: number | null
  logCount: number; lastLogAt: string | null
}

const KIND_ORDER = ['SAT', 'TOEFL', 'AP', 'IELTS']
const KIND_LABEL: Record<string, string> = { AP: 'AP 课程', SAT: 'SAT', TOEFL: '托福', IELTS: '雅思' }

export default function Academics({ role }: { role: 'carson' | 'neo' }) {
  const [subjects, setSubjects] = useState<Subject[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [openId, setOpenId] = useState<number | null>(null)

  const load = useCallback(async () => {
    const r = await api.get<{ subjects: Subject[] }>('/api/academics/dashboard')
    if (r.ok) { setSubjects(r.data.subjects); setError(null) } else setError(r.message)
  }, [])
  useEffect(() => { void load() }, [load])

  if (error) return <Center>读不到学业数据：{error}</Center>
  if (!subjects) return <Center>正在读取…</Center>

  const groups = KIND_ORDER.map((k) => ({ kind: k, items: subjects.filter((s) => s.kind === k) }))
    .filter((g) => g.items.length > 0)
  const todo = subjects.filter((s) => s.placeholder).length

  return (
    <div className="safe-b" style={{ display: 'grid', gap: 18, padding: '8px 16px 28px', maxWidth: 520, margin: '0 auto', width: '100%' }}>
      {todo > 0 && (
        <div className="rise rise-1" style={{
          padding: '16px 18px', borderRadius: 18, fontSize: 15.5, lineHeight: 1.75,
          background: 'var(--ps-accent-soft)', border: '1px solid var(--ps-accent-line)',
        }}>
          还有 <b className="num" style={{ color: 'var(--ps-accent)', fontWeight: 800 }}>{todo}</b> 项要补：
          目标分、考试日期。填完这一页才算真正开始。
        </div>
      )}

      {groups.map((g, gi) => (
        <section key={g.kind} className={`rise rise-${Math.min(gi + 1, 4)}`} style={{ display: 'grid', gap: 8 }}>
          <span style={{
            fontSize: 14, letterSpacing: '.08em',
            color: 'var(--ps-text3)', fontWeight: 700, paddingLeft: 4,
          }}>{KIND_LABEL[g.kind] ?? g.kind}</span>
          {g.items.map((s) => (
            <Card key={s.id} s={s} role={role} open={openId === s.id}
              onToggle={() => setOpenId(openId === s.id ? null : s.id)} onChanged={load} />
          ))}
        </section>
      ))}
    </div>
  )
}

function Center({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: 40, textAlign: 'center', color: 'var(--ps-text3)', fontSize: 14 }}>{children}</div>
}

function Card({ s, role, open, onToggle, onChanged }: {
  s: Subject; role: 'carson' | 'neo'; open: boolean; onToggle(): void; onChanged(): void
}) {
  const urgent = s.daysToExam !== null && s.daysToExam > 0 && s.daysToExam <= 45
  return (
    <div style={{
      background: 'var(--ps-surface)', borderRadius: 18,
      border: `1px solid ${urgent ? 'var(--ps-accent-line)' : 'var(--ps-line)'}`,
      overflow: 'hidden',
    }}>
      <button onClick={onToggle} aria-expanded={open} style={{
        width: '100%', textAlign: 'left', padding: '17px 18px 16px', display: 'grid', gap: 13,
        background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer',
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 18, fontWeight: 700, color: s.placeholder ? 'var(--ps-text3)' : 'var(--ps-text)' }}>
            {s.name}
          </span>
          <span className="num" style={{
            marginLeft: 'auto', fontSize: 36, fontWeight: 900, letterSpacing: '-.045em', lineHeight: 1,
            // 还没出分时别用 900 字重的破折号 —— 那会变成一根粗横线，比数字还抢眼
            ...(s.latestScore ? {} : { fontSize: 22, fontWeight: 600, color: 'var(--ps-text4)' }),
          }}>
            {s.latestScore ? s.latestScore.score : '尚无'}
          </span>
          {s.targetScore && (
            <span className="num" style={{ fontSize: 14.5, color: 'var(--ps-text3)', paddingBottom: 4 }}>
              /{s.targetScore}{s.targetStretch ? `–${s.targetStretch}` : ''}
            </span>
          )}
        </div>

        {s.ratio !== null ? <Bar ratio={s.ratio} target={s.targetRatio} />
          : s.latestScore ? <span style={{ fontSize: 13.5, color: 'var(--ps-text3)' }}>分数区间未确认，暂不画进度</span>
          : null}

        {s.sections.length > 0 && (
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {s.sections.map((x) => (
              <span key={x.section} className="num" style={{
                fontSize: 13.5, padding: '5px 12px', borderRadius: 999,
                background: 'var(--ps-surface2)', color: 'var(--ps-text2)',
              }}>{x.section} {x.score}</span>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 14, fontSize: 13.5, color: 'var(--ps-text3)' }}>
          {s.daysToExam !== null && s.daysToExam > 0 ? (
            <span className="num" style={urgent ? { color: 'var(--ps-accent)', fontWeight: 700 } : undefined}>
              {s.examDate} · {s.daysToExam} 天
            </span>
          ) : <span>考期待定</span>}
          {s.logCount > 0 && <span className="num">{s.logCount} 条记录</span>}
        </div>
      </button>

      {open && <Body s={s} role={role} onChanged={onChanged} />}
    </div>
  )
}

function Bar({ ratio, target }: { ratio: number; target: number | null }) {
  return (
    <div style={{ position: 'relative', height: 9, borderRadius: 99, background: 'var(--ps-line)' }}>
      <div style={{
        position: 'absolute', inset: 0, width: `${ratio * 100}%`, borderRadius: 99,
        background: 'var(--ps-accent)', transition: 'width .6s cubic-bezier(.2,.8,.3,1)',
      }} />
      {target !== null && (
        <div aria-hidden style={{
          position: 'absolute', top: -3, height: 15, width: 2, borderRadius: 2,
          left: `calc(${target * 100}% - 1px)`, background: 'var(--ps-text2)',
        }} />
      )}
    </div>
  )
}

function Body({ s, role, onChanged }: { s: Subject; role: 'carson' | 'neo'; onChanged(): void }) {
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const canEdit = role === 'neo' || s.placeholder
  const [name, setName] = useState(s.placeholder ? '' : s.name)
  const [target, setTarget] = useState(s.targetScore ?? '')
  const [examDate, setExamDate] = useState(s.examDate ?? '')
  const [metric, setMetric] = useState('problems')
  const [value, setValue] = useState('')

  const saveInfo = async () => {
    setBusy(true); setMsg(null)
    const body: Record<string, unknown> = {}
    if (name.trim()) body.name = name.trim()
    if (target.trim()) body.target_score = target.trim()
    if (examDate) body.exam_date = examDate
    const r = await api.patch(`/api/academics/subjects/${s.id}`, body)
    setBusy(false)
    if (r.ok) { setMsg('存好了'); onChanged() } else setMsg(r.message)
  }
  const addLog = async () => {
    if (!value.trim()) return
    setBusy(true); setMsg(null)
    const r = await api.post('/api/academics/logs', {
      subject_id: s.id, metric, value: Number(value),
      unit: metric === 'problems' ? '道' : metric === 'study_minutes' ? '分钟' : null,
    })
    setBusy(false)
    if (r.ok) { setValue(''); setMsg('记下了'); onChanged() } else setMsg(r.message)
  }

  return (
    <div style={{ padding: '0 16px 16px', display: 'grid', gap: 14, borderTop: '1px solid var(--ps-line-soft)' }}>
      {canEdit && (
        <div style={{ display: 'grid', gap: 8, paddingTop: 14 }}>
          <Label>{s.placeholder ? '这门课是什么？' : '课程名'}</Label>
          <Field v={name} on={setName} ph="比如 AP Calculus BC" />
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1, display: 'grid', gap: 6 }}><Label>目标分</Label><Field v={target} on={setTarget} ph="5" /></div>
            <div style={{ flex: 1.3, display: 'grid', gap: 6 }}><Label>考试日期</Label><Field v={examDate} on={setExamDate} type="date" /></div>
          </div>
          <Pill onClick={saveInfo} disabled={busy}>保存课程信息</Pill>
        </div>
      )}

      <div style={{ display: 'grid', gap: 8, paddingTop: canEdit ? 0 : 14 }}>
        <Label>记一笔进度</Label>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={metric} onChange={(e) => setMetric(e.target.value)} aria-label="记录类型" style={field}>
            <option value="problems">刷题</option>
            <option value="study_minutes">学习分钟</option>
            <option value="chapter">章节</option>
            <option value="mock_score">模考</option>
          </select>
          <Field v={value} on={setValue} ph="数量" type="number" />
        </div>
        <Pill onClick={addLog} disabled={busy || !value.trim()}>记下来</Pill>
      </div>

      {msg && <p role="status" style={{ margin: 0, fontSize: 12.5, color: 'var(--ps-text3)' }}>{msg}</p>}
    </div>
  )
}

const field: React.CSSProperties = {
  flex: 1, minWidth: 0, padding: '11px 13px', fontSize: 16, borderRadius: 12,
  border: '1px solid var(--ps-line)', background: 'var(--ps-surface-input)', color: 'var(--ps-text)',
  fontFamily: 'inherit',
}
function Field({ v, on, ph, type = 'text' }: { v: string; on(x: string): void; ph?: string; type?: string }) {
  return <input type={type} value={v} placeholder={ph} onChange={(e) => on(e.target.value)} style={field} />
}
function Label({ children }: { children: React.ReactNode }) {
  return <span style={{ fontSize: 13.5, color: 'var(--ps-text3)' }}>{children}</span>
}
function Pill({ onClick, disabled, children }: { onClick(): void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      minHeight: 52, borderRadius: 999, border: 'none', cursor: 'pointer',
      background: 'var(--ps-accent)', color: 'var(--ps-on-accent)', fontSize: 16, fontWeight: 800,
    }}>{children}</button>
  )
}
